import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface ProgressDistributionChartProps {
  data: Record<string, number>;
  loading?: boolean;
}

const ProgressDistributionChart: React.FC<ProgressDistributionChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Distribution</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No progress data available
        </div>
      </div>
    );
  }

  // Transform data for chart
  const chartData = Object.entries(data).map(([range, count]) => ({
    range: range.replace('-', '-'),
    count,
    percentage: ((count / Object.values(data).reduce((sum, val) => sum + val, 0)) * 100).toFixed(1)
  }));

  const COLORS = {
    '0-25': '#ef4444',   // Red for low progress
    '25-50': '#f59e0b',  // Orange for medium-low progress
    '50-75': '#3b82f6',  // Blue for medium-high progress
    '75-100': '#10b981'  // Green for high progress
  };

  const total = Object.values(data).reduce((sum, val) => sum + val, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="range" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 2']}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} workshops (${props.payload.percentage}%)`,
                'Count'
              ]}
              labelFormatter={(label) => `Progress Range: ${label}%`}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[entry.range as keyof typeof COLORS] || '#6b7280'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Progress Ranges</h4>
          <div className="space-y-1">
            {chartData.map((item) => (
              <div key={item.range} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded mr-2" 
                    style={{ backgroundColor: COLORS[item.range as keyof typeof COLORS] || '#6b7280' }}
                  ></div>
                  <span>{item.range}%</span>
                </div>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Total Workshops:</span>
              <span className="font-medium">{total}</span>
            </div>
            <div className="flex justify-between">
              <span>High Progress (75%+):</span>
              <span className="font-medium text-green-600">{data['75-100'] || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Needs Attention (&lt;50%):</span>
              <span className="font-medium text-red-600">{(data['0-25'] || 0) + (data['25-50'] || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressDistributionChart;


